import type { TFunction } from 'i18next';
import numeral from 'numeral';

import type { AggregatedStat, IFormAggregatedStats } from '@feathr/blackbox';
import type { TStatBoxV2Change } from '@feathr/components';
import { moment, TimeFormat } from '@feathr/hooks';

export type TPreset = '7d' | '30d' | '90d' | 'custom';

export type TDateRange = [start: string, end: string];

export interface IKpiTileDef {
  /** Stable key for React lists. */
  key: string;
  /** Localized label for the tile. */
  label: (t: TFunction) => string;
  /** Pre-formatted value for StatBox's `primary`. */
  primary: (stats: AggregatedStat | undefined) => string;
  /** Pre-formatted subline shown below the primary + delta. */
  subline: (stats: AggregatedStat | undefined, t: TFunction) => string;
  /** Percent change vs. the previous window. null when indeterminate (previous is 0 or missing). */
  deltaPercent: (
    current: AggregatedStat | undefined,
    previous: AggregatedStat | undefined,
  ) => number | null;
  /** Optional tooltip surfaced next to the label. */
  tooltip?: (t: TFunction) => string;
}

/**
 * Typed getter for form-aggregated stat fields. Unlike `stats.get('name')`, this
 * returns a value narrowed to `IFormAggregatedStats[K]` so tile definitions can
 * reference donation-specific fields without casting at every call site.
 *
 * @param stats - The aggregated stat model (or undefined while loading).
 * @param key - The field name to read.
 * @returns The field value, or `undefined` if the stat or field is absent.
 */
export function getFormStat<K extends keyof IFormAggregatedStats>(
  stats: AggregatedStat | undefined,
  key: K,
): IFormAggregatedStats[K] | undefined {
  if (!stats) {
    return undefined;
  }
  return stats.get(key as never) as IFormAggregatedStats[K] | undefined;
}

/**
 * Computes the percent change from a previous value to a current value.
 *
 * @param current - The current-period value.
 * @param previous - The previous-period value.
 * @returns The percent change (e.g., 12.5 for +12.5%), or `null` when previous is missing or zero.
 * @example
 * percentChange(110, 100); // 10
 * percentChange(90, 100);  // -10
 * percentChange(10, 0);    // null
 */
export function percentChange(current?: number, previous?: number): number | null {
  if (current === undefined || previous === undefined || previous === 0) {
    return null;
  }
  return ((current - previous) / previous) * 100;
}

/**
 * Maps a signed percent-change number to StatBoxV2's change direction.
 *
 * @param delta - The percent change, or `null` when indeterminate.
 * @returns `'increase'`, `'decrease'`, `'none'`, or `undefined` when delta is null.
 */
export function deltaToChange(delta: number | null): TStatBoxV2Change | undefined {
  if (delta === null) {
    return undefined;
  }
  if (delta > 0) {
    return 'increase';
  }
  if (delta < 0) {
    return 'decrease';
  }
  return 'none';
}

/**
 * Formats the absolute percent-change value for StatBoxV2's `changeValue` prop.
 *
 * @param delta - The percent change, or `null` when indeterminate.
 * @returns A formatted string like `"12.30%"`, or `undefined` when delta is null so the caller omits the change indicator.
 */
export function formatDeltaValue(delta: number | null): string | undefined {
  if (delta === null) {
    return undefined;
  }
  return numeral(Math.abs(delta) / 100).format('0.00%');
}

/**
 * Computes the immediately-preceding date window of equal length.
 * Given `[start, end]`, returns `[prevStart, prevEnd]` where:
 *  - `prevEnd = start − 1 day`
 *  - `prevStart = prevEnd − (end − start)`
 *
 * @param range - The current window as `[startISO, endISO]` (YYYY-MM-DD).
 * @returns The immediately-preceding window of equal length.
 */
export function shiftWindow([start, end]: TDateRange): TDateRange {
  const startMoment = moment.utc(start, TimeFormat.isoDate);
  const endMoment = moment.utc(end, TimeFormat.isoDate);
  const spanDays = endMoment.diff(startMoment, 'days');
  const prevEnd = startMoment.clone().subtract(1, 'day');
  const prevStart = prevEnd.clone().subtract(spanDays, 'days');
  return [prevStart.format(TimeFormat.isoDate), prevEnd.format(TimeFormat.isoDate)];
}

/**
 * Resolves a preset to a concrete `[start, end]` window.
 * For `'custom'`, the caller supplies the range — this function returns the passed-through range.
 *
 * @param preset - The selected preset.
 * @param currentRange - The current range (used as the fallback for `'custom'`).
 * @returns The resolved `[startISO, endISO]` window.
 */
export function presetToRange(preset: TPreset, currentRange: TDateRange): TDateRange {
  if (preset === 'custom') {
    return currentRange;
  }
  const today = moment.utc().format(TimeFormat.isoDate);
  const daysBack = preset === '7d' ? 7 : preset === '30d' ? 30 : 90;
  const start = moment.utc().subtract(daysBack, 'days').format(TimeFormat.isoDate);
  return [start, today];
}

/**
 * Formats a donation count for display. The canonical count formatter for the
 * donation form report — every surface in the report (KPI tiles, trend chart
 * tooltips, the type-distribution table) must render counts identically.
 *
 * @param value - Count to format; `undefined` is treated as 0.
 * @returns Formatted count string (e.g. `"28,450"`).
 */
export function formatCount(value: number | undefined): string {
  return numeral(value ?? 0).format('0,0');
}

/**
 * Formats a dollar amount for display. The canonical currency formatter for the
 * donation form report — every surface in the report (KPI tiles, trend chart
 * tooltips, the type-distribution table) must render amounts identically, down
 * to the cent.
 *
 * @param value - Dollar amount to format; `undefined` is treated as 0.
 * @returns Formatted currency string (e.g. `"$1,163.80"`).
 */
export function formatCurrency(value: number | undefined): string {
  return numeral(value ?? 0).format('$0,0.00');
}

function formatRate(value: number | undefined): string {
  return numeral(value ?? 0).format('0.00%');
}

function conversionRate(stats: AggregatedStat | undefined): number {
  const views = getFormStat(stats, 'num_views') ?? 0;
  const donations = getFormStat(stats, 'donations_count') ?? 0;
  if (views === 0) {
    return 0;
  }
  return donations / views;
}

export function oneTimeTotal(stats: AggregatedStat | undefined): number {
  const total = getFormStat(stats, 'donations_total') ?? 0;
  const recurring = getFormStat(stats, 'donations_recurring_total') ?? 0;
  return Math.max(total - recurring, 0);
}

export function oneTimeCount(stats: AggregatedStat | undefined): number {
  const count = getFormStat(stats, 'donations_count') ?? 0;
  const recurring = getFormStat(stats, 'donations_recurring_count') ?? 0;
  return Math.max(count - recurring, 0);
}

/**
 * Breakdown of donations by type (one-time vs. monthly/recurring) from
 * aggregated stats. Consumed by the Breakdown tab's Donation Type Distribution
 * donut chart and its side table.
 */
export interface IBreakdownData {
  oneTime: { count: number; total: number };
  monthly: { count: number; total: number };
}

/**
 * Derives the donation-type breakdown from aggregated stats. Monthly values
 * come straight from `donations_recurring_*`; one-time is the non-recurring
 * remainder (clamped to 0 via `oneTime*` helpers).
 */
export function buildBreakdownData(stats: AggregatedStat | undefined): IBreakdownData {
  return {
    oneTime: {
      count: oneTimeCount(stats),
      total: oneTimeTotal(stats),
    },
    monthly: {
      count: getFormStat(stats, 'donations_recurring_count') ?? 0,
      total: getFormStat(stats, 'donations_recurring_total') ?? 0,
    },
  };
}

/** Row 1: four-tile KPIs. */
export const kpiRowOne: IKpiTileDef[] = [
  {
    key: 'total_views',
    label: (t) => t('Total Views'),
    primary: (stats) => formatCount(getFormStat(stats, 'num_views')),
    subline: (stats, t) =>
      t('{{formatted}} unique viewers', {
        formatted: formatCount(getFormStat(stats, 'unique_views')),
      }),
    deltaPercent: (current, previous) =>
      percentChange(getFormStat(current, 'num_views'), getFormStat(previous, 'num_views')),
  },
  {
    key: 'form_completions',
    label: (t) => t('Form Completions'),
    primary: (stats) => formatCount(getFormStat(stats, 'num_submissions')),
    subline: (stats, t) =>
      t('{{rate}} completion rate', { rate: formatRate(getFormStat(stats, 'submission_rate')) }),
    deltaPercent: (current, previous) =>
      percentChange(
        getFormStat(current, 'num_submissions'),
        getFormStat(previous, 'num_submissions'),
      ),
  },
  {
    key: 'total_donations',
    label: (t) => t('Total Donations'),
    primary: (stats) => formatCurrency(getFormStat(stats, 'donations_total')),
    subline: (stats, t) =>
      t('{{amount}} average donation', {
        amount: formatCurrency(getFormStat(stats, 'average_donation')),
      }),
    deltaPercent: (current, previous) =>
      percentChange(
        getFormStat(current, 'donations_total'),
        getFormStat(previous, 'donations_total'),
      ),
  },
  {
    key: 'conversion_rate',
    label: (t) => t('Conversion Rate'),
    primary: (stats) => formatRate(conversionRate(stats)),
    subline: (stats, t) =>
      t('{{formatted}} donations from views', {
        formatted: formatCount(getFormStat(stats, 'donations_count')),
      }),
    deltaPercent: (current, previous) =>
      percentChange(conversionRate(current), conversionRate(previous)),
  },
];

/** Row 2: three-tile KPIs. */
export const kpiRowTwo: IKpiTileDef[] = [
  {
    key: 'growth_credits',
    label: (t) => t('Growth Credits'),
    primary: (stats) => formatCount(getFormStat(stats, 'growth_credits')),
    subline: (stats, t) =>
      t('{{formatted}} donations as marketing credits', {
        formatted: formatCount(getFormStat(stats, 'donations_credits_count')),
      }),
    deltaPercent: (current, previous) =>
      percentChange(
        getFormStat(current, 'growth_credits'),
        getFormStat(previous, 'growth_credits'),
      ),
  },
  {
    key: 'recurring_donations',
    label: (t) => t('Recurring Donations'),
    primary: (stats) => formatCurrency(getFormStat(stats, 'donations_recurring_total')),
    subline: (stats, t) =>
      t('{{formatted}} donations', {
        formatted: formatCount(getFormStat(stats, 'donations_recurring_count')),
      }),
    deltaPercent: (current, previous) =>
      percentChange(
        getFormStat(current, 'donations_recurring_total'),
        getFormStat(previous, 'donations_recurring_total'),
      ),
  },
  {
    key: 'one_time_donations',
    label: (t) => t('One-Time Donations'),
    primary: (stats) => formatCurrency(oneTimeTotal(stats)),
    subline: (stats, t) =>
      t('{{formatted}} donations', { formatted: formatCount(oneTimeCount(stats)) }),
    deltaPercent: (current, previous) =>
      percentChange(oneTimeTotal(current), oneTimeTotal(previous)),
  },
];
